import { Box } from '@mui/material';
import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../../../AppContext';
import { Actions } from '../../../enums/ActionEnums';
import {
  IMeetingsMeetingType,
  useGetMeetingTypesQuery,
  useDeleteMeetingTypeMutation,
  GetMeetingTypesDocument,
  IGetMeetingTypesQuery
} from '../../../generated/graphql';
import Loader from '../../Common/Loader';
import OverviewTable, { IHeadCell } from '../../Common/Tables/OverviewTable';
import MeetingTypeForm from './MeetingTypeForm';

const MeetingTypeList: React.FunctionComponent = () => {
  const { data, loading } = useGetMeetingTypesQuery();
  const [allData, setAllData] = useState<IMeetingsMeetingType[]>([]);
  const [openForm, setOpenForm] = useState(false);
  const [meetingType, setMeetingType] = useState<IMeetingsMeetingType | null>(null);
  const { dispatch } = useContext(AppContext);
  const [deleteMeetingType] = useDeleteMeetingTypeMutation();

  useEffect(() => {
    if (data && data.result) {
      setAllData(data.result as IMeetingsMeetingType[]);
    }
  }, [data]);

  const handleRemove = (rowData: IMeetingsMeetingType) => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: `Are you sure you want to delete ${rowData.name} meeting type?`,
        agreeAction: handleDelete,
        params: rowData
      }
    });
  };

  const handleDelete = (rowData: IMeetingsMeetingType) => {
    if (!rowData.id) {
      return;
    }
    dispatch({ type: Actions.HideConfirmation });
    deleteMeetingType({
      variables: {
        id: rowData.id
      },
      update(cache) {
        dispatch({
          type: Actions.ShowMessage,
          payload: { text: `Meeting Type ${rowData.name} deleted`, severity: 'success' }
        });

        setAllData((prevData) => prevData.filter((m) => m?.id !== rowData.id));

        const meetingTypesQuery = cache.readQuery({
          query: GetMeetingTypesDocument
        }) as IGetMeetingTypesQuery;
        if (meetingTypesQuery.result) {
          const cachedMeetingType = meetingTypesQuery.result.findIndex((m) => m?.id === rowData.id);
          if (cachedMeetingType > -1) {
            const updatedData = meetingTypesQuery.result.filter(function (el) {
              return el?.id !== rowData.id;
            });
            cache.writeQuery({
              query: GetMeetingTypesDocument,
              data: { result: updatedData }
            });
          }
        }
      }
    }).catch((e) => {
      dispatch({
        type: Actions.ShowMessage,
        payload: { text: e.message, severity: 'error', autoHide: 6000 }
      });
    });
  };

  const columns = [
    {
      title: 'Details',
      width: 63,
      sorting: false
    },
    {
      title: 'Id',
      field: 'id',
      type: 'numeric',
      width: 130
    },
    {
      title: 'Meeting Type Name',
      field: 'name',
      width: 1455
    },
    {
      title: 'Remove',
      width: 73,
      sorting: false
    }
  ] as IHeadCell[];

  const handleDetails = (rowData: IMeetingsMeetingType) => {
    setMeetingType(rowData);
    setOpenForm(true);
  };

  const handleClose = (
    saved: boolean,
    isNew?: boolean,
    meetingTypeInput?: IMeetingsMeetingType
  ) => {
    if (saved && meetingTypeInput) {
      if (isNew) {
        setAllData([meetingTypeInput, ...allData]);
      } else {
        setAllData(allData.map((m) => (m?.id === meetingTypeInput.id ? meetingTypeInput : m)));
      }
    }
    setMeetingType(null);
    setOpenForm(false);
  };

  return (
    <Box className="overviewPage" sx={{ height: '100%' }}>
      <Box className="print\:w-full">
        <OverviewTable
          columns={columns}
          data={allData}
          loading={loading}
          handleDetails={handleDetails}
          handleRemove={handleRemove}
          pageSize={15}
          createButtonProps={{
            id: 'create-meeting-type-button',
            onClick: () => setOpenForm(true),
            text: 'Create Meeting Type',
            disabled: false
          }}
        />
        {openForm && <MeetingTypeForm meetingTypeInput={meetingType} handleClose={handleClose} />}
        <Loader loading={loading} />
      </Box>
    </Box>
  );
};

export default MeetingTypeList;
